import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { XIcon } from "@assets/icons";
import { ModalProps } from "./Modal.props";
import P from "../P/P";

const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	children,
	header,
	showCloseButton = true,
	containerClassName = "",
	contentClassName = "",
}): JSX.Element => {
	const isStringHeader = typeof header === "string";

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					className="fixed inset-0 z-[99] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
					onMouseDown={() => onClose()}
				>
					<motion.div
						initial={{ scale: 0.96, opacity: 0, y: 20 }}
						animate={{ scale: 1, opacity: 1, y: 0 }}
						exit={{ scale: 0.96, opacity: 0, y: 20 }}
						transition={{ duration: 0.25 }}
						className={`
							relative w-[calc(100vw-16px)] minPhone:w-[calc(100vw-24px)] sm:w-full
							max-w-[calc(100vw-16px)] minPhone:max-w-[calc(100vw-24px)] sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-3xl TwoXl:max-w-7xl
							max-h-[92vh] sm:max-h-[95vh]
							overflow-hidden rounded-t-2xl sm:rounded-2xl
							bg-light-bgColor dark:bg-dark-bgColor shadow-lg
							${containerClassName}
						`}
						onMouseDown={(e) => e.stopPropagation()}
						onClick={(e) => e.stopPropagation()}
					>
						{header &&
							(isStringHeader ? (
								<div className="grid grid-cols-[1fr,auto] items-center gap-3 border-b border-stroke px-4 py-3 sm:px-6 sm:py-4">
									<P size="p1" type="semibold" className="min-w-0 break-words">
										{header}
									</P>

									{showCloseButton && (
										<button
											type="button"
											onClick={onClose}
											className="shrink-0 text-gray-600 transition hover:text-gray-800 focus:outline-none"
										>
											<SvgIcon Icon={XIcon} width={20} height={20} />
										</button>
									)}
								</div>
							) : (
								<div
									className={`grid items-center gap-3 border-b border-stroke px-4 py-3 sm:px-6 sm:py-4 ${
										header?.left
											? "grid-cols-[auto,1fr,auto]"
											: "grid-cols-[1fr,auto]"
									}`}
								>
									{header?.left && (
										<div className="shrink-0">{header.left}</div>
									)}

									<div
										className={`min-w-0 ${
											header?.left ? "text-center" : "text-left"
										}`}
									>
										{header?.center}
									</div>

									{showCloseButton && (
										<button
											type="button"
											onClick={onClose}
											className="shrink-0 text-gray-600 transition hover:text-gray-800 focus:outline-none"
										>
											<SvgIcon Icon={XIcon} width={20} height={20} />
										</button>
									)}
								</div>
							))}

						<div
							className={`max-h-[calc(92vh-64px)] overflow-y-auto px-4 py-4 text-textColor sm:max-h-[calc(95vh-72px)] sm:px-6 sm:py-4 ${contentClassName}`}
						>
							{children}
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default React.memo(Modal);
